import {CurrentRepositoryProvider} from '@github-ui/current-repository'
import {activityIndexPath} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useQuery} from '@github-ui/react-query'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useNavigate} from '@github-ui/use-navigate'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {PageLayout, Pagination, Spinner} from '@primer/react'
import {Box, Heading, Link, Text} from '@primer/styled-react'
import React from 'react'

import {ActivityBlankSlate} from '../components/ActivityBlankSlate'
import {ActivityCard} from '../components/ActivityCard'
import {ActivityFilterBar} from '../components/ActivityFilterBar'
import {useBaseUrl} from '../contexts/BaseUrlContext'
import {PerPageProvider} from '../contexts/PerPageContext'
import type {ActivityIndexPagePayload} from '../types/activity-types'

export default function ActivityIndex() {
  const navigate = useNavigate()
  const baseUrl = useBaseUrl()

  // When baseUrl is present means we're rendering this page from Stafftools
  // And then we want the full SHA instead of the shortened one
  const showFullShas = baseUrl ? true : false

  const routePayload = useRoutePayload<ActivityIndexPagePayload>()
  const {repo, refInfo, feedbackUrl} = routePayload

  const currentUrl = ssrSafeWindow?.location.href

  const {
    data: deferredActivityList,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['activity-list', currentUrl],
    queryFn: async () => {
      const url = new URL(currentUrl || '', window.location.origin)
      url.searchParams.set('deferred', 'true')
      const response = await verifiedFetchJSON(url.toString())

      if (response.ok) {
        try {
          return (await response.json()) as {payload: ActivityIndexPagePayload}
        } catch {
          throw new Error('Failed to load activity data.')
        }
      } else if (response.status === 408) {
        throw new Error('Your request timed out. Try changing filters or choosing a shorter timeframe.')
      } else {
        throw new Error('Failed to load activity data.')
      }
    },
    select: activityData => activityData.payload.activityList,
    enabled: !!currentUrl && !!routePayload.activityList.asyncLoad,
  })

  const activityList = deferredActivityList || routePayload.activityList

  // The pagination control assumes we know a current page and number of pages
  // With pushes, we don't pre-fetch that data. We only check to see if there is a
  // a previous page of pushes or a next page of pushes. So we will make use of pageCount
  // and currentPage to control whether the previous or next page links are enabled.

  // If there are additional pages of pushes, we will use a page count of 3 to mimic a next/previous page.
  // If there aren't additional pages of pushes, then we only have 1 page.
  const pageCount = activityList.hasNextPage || activityList.hasPreviousPage ? 3 : 1

  // Since we are using 3 pages to represent existance of a next or previous page, we will set currentPage to 1
  // if there is no previous page, which will disable the "previous" button. If there is no next page
  // then we will set currentPage to 3, which will disable the "next" button. Otherwise we will set currentPage
  // to 2 which will enable both buttons.
  // If there is only one page of pushes, pageCount will be 1. There will be no previous page, which will set
  // currentPage to 1. This will disable both the "previous" and "next" buttons.
  const currentPage = !activityList.hasPreviousPage ? 1 : !activityList.hasNextPage ? 3 : 2

  const onPageChange = React.useCallback(
    (e: React.MouseEvent, p: number) => {
      window.scrollTo(0, 0)
      // If the user clicks "previous" then we will get p = 1 and currentPage = 2 or p = 2 and currentPage = 3.
      // If the user clicks "next" then we will get p = 2 and currentPage = 1 or p = 3 and currentPage = 2.
      navigate(
        activityIndexPath({
          repo,
          baseUrl,
          branch: refInfo.name,
          filter: activityList,
          pagination:
            p < currentPage
              ? {before: activityList.startCursor || activityList.cursor}
              : {after: activityList.endCursor || activityList.cursor},
          perPage: activityList.perPage,
        }),
      )

      e.preventDefault()
    },
    [navigate, repo, baseUrl, refInfo.name, activityList, currentPage],
  )

  return (
    <CurrentRepositoryProvider repository={repo}>
      <PerPageProvider perPage={activityList.perPage}>
        <PageLayout style={{minHeight: '500px'}}>
          <PageLayout.Header divider="line" style={{marginBottom: '16px'}}>
            <Heading as="h1" sx={{fontSize: 4, fontWeight: 'normal', mt: 2, lineHeight: 1}}>
              Activity
            </Heading>
          </PageLayout.Header>
          <PageLayout.Content>
            <ActivityFilterBar repo={repo} refInfo={refInfo} filter={activityList} />
            {/* Outer box holding pushes list */}
            <Box
              data-hpc
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                borderColor: 'border.default',
                borderStyle: 'solid',
                borderWidth: 1,
                mt: 3,
              }}
            >
              {isFetching ? (
                <div className="flex tmp-p-6 text-center">
                  <Spinner srText="Loading activity" />
                </div>
              ) : activityList.items.length > 0 ? (
                <>
                  {activityList.items.map((push, index) => (
                    <ActivityCard
                      // eslint-disable-next-line @eslint-react/no-array-index-key
                      key={index}
                      push={push}
                      repo={repo}
                      showRefInfo={refInfo.name === ''}
                      showFullShas={showFullShas}
                    />
                  ))}
                </>
              ) : error ? (
                <ActivityBlankSlate heading="Failed to get activity" description={error.message} />
              ) : (
                <ActivityBlankSlate />
              )}
            </Box>
          </PageLayout.Content>
          <PageLayout.Footer>
            {feedbackUrl ? (
              <div>
                <Link href={feedbackUrl} target="_blank" sx={{ml: 2}}>
                  <Text sx={{fontSize: 1, display: ['none', 'inline', 'inline']}}>Share feedback about this page</Text>
                  <Text sx={{fontSize: 0, display: ['inline', 'none', 'none']}}>Share feedback</Text>
                </Link>
              </div>
            ) : null}
            {(activityList.hasNextPage || activityList.hasPreviousPage) && (
              <Pagination
                pageCount={pageCount}
                currentPage={currentPage}
                onPageChange={onPageChange}
                showPages={false}
              />
            )}
          </PageLayout.Footer>
        </PageLayout>
      </PerPageProvider>
    </CurrentRepositoryProvider>
  )
}
